/**
 * Tiny SVG sparkline. Zero-dependency, viewbox-based so it scales cleanly
 * inside any card. Nulls in the input series are treated as gaps (line
 * broken across missing values) rather than as zero — critical for %-style
 * metrics where "no data" ≠ "0%".
 *
 * Props:
 *   values   Array<number|null>  — bucketed time series
 *   color    string              — line stroke
 *   median   number|null         — draws a dashed benchmark line if provided
 *   height   number              — px, defaults 28
 */
export default function Sparkline({ values, color = "#FF3B30", median = null, height = 28, testId }) {
  if (!values || values.length === 0) {
    return <div style={{ height }} data-testid={testId} />;
  }
  const width = 100; // percent-ish coordinate space; final width via CSS
  const nums = values.map((v) => (typeof v === "number" ? v : null));
  const hasAny = nums.some((v) => v !== null);
  if (!hasAny) {
    return (
      <div className="text-[9px] text-zinc-600 uppercase tracking-widest" style={{ height }} data-testid={testId}>
        no trend data
      </div>
    );
  }

  const knownVals = nums.filter((v) => v !== null);
  const rawMin = Math.min(...knownVals, median ?? Infinity);
  const rawMax = Math.max(...knownVals, median ?? -Infinity);
  // Pad the vertical scale so a flat series still shows something.
  const pad = Math.max((rawMax - rawMin) * 0.15, 0.5);
  const min = rawMin - pad;
  const max = rawMax + pad;
  const range = max - min || 1;

  const step = nums.length > 1 ? width / (nums.length - 1) : width;
  const y = (v) => height - ((v - min) / range) * height;

  // Build path with "M / L" commands, breaking across nulls so gaps show.
  let d = "";
  let penDown = false;
  nums.forEach((v, i) => {
    if (v === null) { penDown = false; return; }
    const x = i * step;
    const yy = y(v);
    d += `${penDown ? "L" : "M"}${x.toFixed(2)},${yy.toFixed(2)} `;
    penDown = true;
  });

  const lastKnownIdx = (() => {
    for (let i = nums.length - 1; i >= 0; i--) if (nums[i] !== null) return i;
    return -1;
  })();
  const lastX = lastKnownIdx >= 0 ? lastKnownIdx * step : null;
  const lastY = lastKnownIdx >= 0 ? y(nums[lastKnownIdx]) : null;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      style={{ width: "100%", height, display: "block", overflow: "visible" }}
      data-testid={testId}
      role="img"
      aria-label="Trend sparkline"
    >
      {median != null && (
        <line
          x1={0}
          x2={width}
          y1={y(median)}
          y2={y(median)}
          stroke="#71717A"
          strokeDasharray="2 2"
          strokeWidth={0.6}
          data-testid={`${testId}-benchmark`}
        />
      )}
      <path d={d.trim()} stroke={color} strokeWidth={1.2} fill="none" strokeLinejoin="round" strokeLinecap="round" />
      {lastX != null && (
        <circle cx={lastX} cy={lastY} r={1.6} fill={color} />
      )}
    </svg>
  );
}
